/**
 * Scottsy — Mobile product-preview manager (Teeinblue)
 * (cache-bust 2026-09-04)
 * ---------------------------------------------------------------------------
 * Mobile <=750px only; desktop keeps the CSS sticky column.
 *   FULL   — top of page: preview full width.
 *   DOCKED — scrolled into the form: compact thumbnail with a small ⤢ icon.
 *   HIDDEN — a KEYBOARD field is focused: preview slides away (auto-returns
 *            when the keyboard is dismissed).
 *   LIGHTBOX — tapping ⤢ opens the live preview as a Teeinblue-styled modal
 *            (dark backdrop + ✕). View on demand; obvious way back.
 * Pairs with sticky-preview.css.
 */
(function () {
  'use strict';

  var MOBILE_MAX = 750;
  var DOCK_AT = 140;
  var UNDOCK_AT = 70;
  var DOCKED_WIDTH = '60%';
  var REAPPLY_DELAY = 140;
  var REAPPLY_MAXWAIT = 600;
  var KB_GAP = 150;
  var KEYBOARD_TYPES = { text: 1, search: 1, url: 1, email: 1, tel: 1, number: 1, password: 1 };

  var built = false, startRequested = false;

  function isMobile() { return window.innerWidth <= MOBILE_MAX; }

  // ========================================================================
  // ADD-TO-CART SUBMIT GUARD — attached at load, BEFORE build(), on ALL widths.
  // The personalisation fields live inside <form class="tee-form-wrapper"
  // action="/cart/add"> whose default button is "Add to cart", so a bare Return
  // on ANY single-line input in that form implicitly submits it (premature
  // add-to-cart with an incomplete Teeinblue config). Block it unconditionally.
  // Then, for real keyboard text fields, also drop the keyboard. The location
  // autocomplete keeps Enter (Google Places selects a suggestion) — we still
  // block the form submit for it, we just don't blur it. Scoped to the
  // personaliser form so normal product pages are unaffected.
  // ========================================================================
  var FORM_SEL = 'form.tee-form-wrapper, #teeinblue-wrapper, #tee-artwork-form, .tee-customize-main-form, .tee-customization-wrapper';
  document.addEventListener('keydown', function (e) {
    if (e.isComposing || e.keyCode === 229) return;        // IME (Android Gboard / CJK)
    if (e.key !== 'Enter' && e.keyCode !== 13) return;
    var el = e.target;
    if (!el || el.tagName !== 'INPUT') return;             // textarea Enter = newline
    if (!el.closest(FORM_SEL)) return;                     // only the personaliser form
    e.preventDefault();                                    // never let Return add-to-cart
    if (el.readOnly || el.disabled) return;                // date / proxy inputs: no keyboard
    if (el.classList.contains('tee-maps__input')) return;  // let Places keep Enter for suggestions
    var t = (el.getAttribute('type') || 'text').toLowerCase();
    if (KEYBOARD_TYPES[t] && isMobile()) el.blur();        // drop the on-screen keyboard
  });

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // Build on load if mobile, AND lazily when the viewport later becomes mobile
  // (phone that loaded in landscape >750px then rotated to portrait).
  function requestStart() { if (!built && !startRequested) { startRequested = true; start(0); } }
  onReady(function () { if (isMobile()) requestStart(); });
  window.addEventListener('resize', function () { if (isMobile()) requestStart(); });

  function start(tries) {
    if (built) return;
    var gallery = document.querySelector('.product-gallery');
    var content = document.querySelector('.tee-gallery-content');
    if (!gallery || !content) {
      if (tries < 30) { setTimeout(function () { start(tries + 1); }, 400); return; }
      // Teeinblue slower than the ~12s poll: watch for it appearing rather than
      // relying only on a future resize, so the manager still initialises.
      // document.documentElement, not document.body — confirmed live (see the
      // enforce16px note below) that document.body can get replaced wholesale
      // after this early in the boot sequence, orphaning any observer already
      // attached to the old reference; <html> is never swapped the same way.
      startRequested = false;
      try {
        var mo = new MutationObserver(function () {
          if (document.querySelector('.product-gallery') && document.querySelector('.tee-gallery-content')) {
            mo.disconnect();
            if (isMobile()) requestStart();
          }
        });
        mo.observe(document.documentElement, { childList: true, subtree: true });
      } catch (e) {}
      return;
    }
    built = true;
    build(gallery, content);
  }

  function build(gallery, content) {
    var state = '';          // 'full' | 'docked' | 'hidden' | 'desktop'
    var lightboxOpen = false;
    var blurTimer = null;
    var reapplyTimer = null, reapplyFirst = 0;
    var kbWasOpen = false;
    var lockScrollY = 0, prevBodyTop = '';

    var note = document.createElement('div');
    note.className = 'tb-note';
    note.innerHTML = 'Watermarks are not printed on your final item<br>Scroll down to personalise';
    if (gallery.parentNode) gallery.parentNode.insertBefore(note, gallery.nextSibling);

    var expand = document.createElement('button');
    expand.type = 'button';
    expand.className = 'tb-expand';
    expand.setAttribute('aria-label', 'Enlarge preview');
    expand.innerHTML = '<span class="tb-expand__pill" aria-hidden="true">⤢</span>';
    gallery.appendChild(expand);
    function ensureExpand() { if (!expand.isConnected && gallery.isConnected) gallery.appendChild(expand); }
    /* Same self-heal as ensureExpand(), extended to the lightbox chrome —
       full review caught that backdrop/closeBtn/note were appended once
       here and never revisited, unlike every sibling element in this same
       function. This file's own enforce16px()/dialogOpen() fixes establish
       as confirmed-live fact that document.body can get replaced wholesale
       after early boot-time scripts run; if that happens after this runs,
       backdrop/closeBtn/note become silently detached — invisible,
       unclickable, still referenced by openLightbox()/closeLightbox()'s
       closures but no longer part of the rendered page. Worst case: the
       lightbox locks scroll with no visible backdrop and no reachable ✕. */
    function ensureLightboxChrome() {
      if (!backdrop.isConnected) document.body.appendChild(backdrop);
      if (!closeBtn.isConnected) document.body.appendChild(closeBtn);
      if (!note.isConnected && gallery.isConnected && gallery.parentNode) gallery.parentNode.insertBefore(note, gallery.nextSibling);
    }

    var backdrop = document.createElement('div');
    backdrop.className = 'tb-lightbox-backdrop';
    document.body.appendChild(backdrop);
    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'tb-lightbox-close';
    closeBtn.setAttribute('aria-label', 'Close preview');
    closeBtn.innerHTML = '&times;';
    document.body.appendChild(closeBtn);

    // Re-resolve gallery / content refs if Teeinblue replaces the nodes.
    function nodes() {
      if (!gallery.isConnected) { var g = document.querySelector('.product-gallery'); if (g) gallery = g; }
      if (!content.isConnected) { var c = document.querySelector('.tee-gallery-content'); if (c) content = c; }
    }

    function setWidth(w) {
      nodes();
      content.style.width = w;
      content.style.margin = '0 auto';
      window.dispatchEvent(new Event('resize'));
    }

    function setState(next) {
      if (next === state) return;
      var prev = state;
      state = next;
      nodes();
      gallery.classList.toggle('tb-hidden', next === 'hidden');
      gallery.classList.toggle('tb-docked', next === 'docked');
      note.style.display = (next === 'full') ? '' : 'none';

      if (next === 'hidden') { gallery.classList.remove('tb-morph'); return; }

      if (next === 'docked') setWidth(DOCKED_WIDTH);
      else if (next === 'full') setWidth('100%');

      if ((prev === 'full' && next === 'docked') || (prev === 'docked' && next === 'full')) {
        gallery.classList.remove('tb-morph');
        void gallery.offsetWidth;
        gallery.classList.add('tb-morph');
      }
    }

    // On desktop, tear our inline styles/classes down (never force 100%, which
    // contaminates the theme's desktop column). ALWAYS clear inline (idempotent)
    // so any stray contamination self-heals; uses nodes() for the live element.
    function teardownDesktop() {
      nodes();
      content.style.width = '';
      content.style.margin = '';
      gallery.style.top = '';
      if (state === 'desktop') return;
      state = 'desktop';
      gallery.classList.remove('tb-hidden', 'tb-docked', 'tb-morph');
      note.style.display = 'none';
    }

    function isField(el) {
      if (!el) return false;
      var tag = el.tagName;
      if (tag !== 'INPUT' && tag !== 'TEXTAREA') return false;
      if (el.readOnly || el.disabled) return false;
      if (el.classList.contains('flatpickr-input') ||
          el.classList.contains('tee__input--date-picker') ||
          el.closest('.tee-date-picker-wrapper, .flatpickr-calendar')) return false;
      if (tag === 'INPUT') {
        var t = (el.getAttribute('type') || 'text').toLowerCase();
        if (!KEYBOARD_TYPES[t]) return false;
      }
      return el.classList.contains('tee__input') ||
        !!el.closest('.tee-customization-wrapper, #tee-artwork-form, .tee-customize-main-form, #teeinblue-wrapper');
    }

    /* Bare presence used to be enough, but a closed dialog's own node
       lingers in the DOM for several seconds afterward (confirmed live —
       same root cause as the "closing bug" .vm-transition--overlay-
       leave-active fixes elsewhere in this project: Vue's own
       leave-transition never actually changes a value, so transitionend
       never fires and Vue never removes the node). Every caller here
       wants "is a dialog genuinely blocking things right now", and a
       stale node answered that wrong for as long as it lingered — e.g.
       apply() below bailing out of its own docking/positioning updates
       long after the customer had already closed the dialog.

       Reads computed pointer-events on the dialog root rather than
       matching the .vm--overlay.vm-transition--overlay-leave-active class
       directly — confirmed live that the overlay element itself can
       already be gone (dialog.querySelector('.vm--overlay') → null)
       while the stale dialog root it lived in is still in the DOM, and
       "no overlay to check" is not the same thing as "this one is
       genuinely open". pointer-events is the exact property the CSS
       closing-bug fix already sets to none !important on every dialog
       type while stale/closing (search that fix in scottsy-pdp-v3.css),
       so this reads the same live signal the CSS already committed to. */
    function dialogOpen() {
      /* document.querySelectorAll, not '#teeinblue-wrapper .tee-dialog' —
         confirmed live (on the "MUMMY Letter Tile" product) that
         #teeinblue-wrapper doesn't contain #tee-artwork-form, and
         Vue-js-modal (the library behind every .tee-dialog) portals its
         modals near document.body rather than nesting them inside whatever
         triggered them — so the descendant-scoped query above silently
         matched zero dialogs on this template, and every caller here
         (openLightbox/apply/the focusin handler) would proceed as if no
         dialog were ever open. .tee-dialog is specific enough to Teeinblue
         that scoping it at all was only ever a minor optimisation, never
         load-bearing for correctness. */
      var dialogs = document.querySelectorAll('.tee-dialog');
      for (var i = 0; i < dialogs.length; i++) {
        if (getComputedStyle(dialogs[i]).pointerEvents !== 'none') return true;
      }
      return false;
    }
    function keyboardOpen() {
      var vv = window.visualViewport;
      return !!vv && (window.innerHeight - vv.height) > KB_GAP;
    }

    // --- Lightbox ---
    function openLightbox() {
      if (!isMobile() || lightboxOpen || dialogOpen()) return;
      nodes();
      lightboxOpen = true;
      kbWasOpen = false;
      var a = document.activeElement;
      if (a && a.blur) a.blur();
      gallery.classList.remove('tb-hidden', 'tb-docked', 'tb-morph');
      gallery.classList.add('tb-lightboxed');
      backdrop.classList.add('is-open');
      closeBtn.classList.add('is-open');
      lockScrollY = window.pageYOffset;
      prevBodyTop = document.body.style.top;                 // snapshot foreign lock
      document.body.style.top = (-lockScrollY) + 'px';
      document.body.classList.add('tb-lightbox-lock');
      setWidth('100%');
    }
    // userInitiated: true only for the ✕ / backdrop. On a breakpoint/dialog
    // crossover the stored scroll coordinate is meaningless in the new layout,
    // so we don't restore it (land at top instead of a wrong offset).
    function closeLightbox(userInitiated) {
      if (!lightboxOpen) return;
      lightboxOpen = false;
      kbWasOpen = false;
      gallery.classList.remove('tb-lightboxed');
      gallery.style.top = '';
      backdrop.classList.remove('is-open');
      closeBtn.classList.remove('is-open');
      document.body.classList.remove('tb-lightbox-lock');
      document.body.style.top = prevBodyTop || '';
      if (userInitiated) window.scrollTo(0, lockScrollY);
      state = '';
      apply();
    }

    function apply() {
      nodes();
      if (lightboxOpen) { if (!isMobile() || dialogOpen()) closeLightbox(false); return; }
      if (dialogOpen()) return;
      if (!isMobile()) { teardownDesktop(); return; }
      ensureExpand();
      ensureLightboxChrome();
      if (isField(document.activeElement)) { setState('hidden'); return; }
      var y = window.pageYOffset;
      if (state === 'docked') setState(y > UNDOCK_AT ? 'docked' : 'full');
      else setState(y > DOCK_AT ? 'docked' : 'full');
    }

    function scheduleApply() {
      var now = Date.now();
      if (!reapplyTimer) reapplyFirst = now;
      if (reapplyTimer) clearTimeout(reapplyTimer);
      if (now - reapplyFirst >= REAPPLY_MAXWAIT) { reapplyTimer = null; apply(); return; }
      reapplyTimer = setTimeout(function () { reapplyTimer = null; apply(); }, REAPPLY_DELAY);
    }

    window.addEventListener('scroll', apply, { passive: true });
    window.addEventListener('resize', apply);
    document.addEventListener('click', scheduleApply, true);
    try {
      // document.documentElement, not document.body — see dialogOpen()'s note
      // above (#teeinblue-wrapper existing isn't the same as containing what
      // this needs to watch) AND enforce16px's boot() note below (document.body
      // itself can get replaced wholesale later in the boot sequence, orphaning
      // an observer already attached to the old reference — confirmed live via
      // a diagnostic build that fixAll() was invoked exactly once, at boot,
      // and never again despite the page settling for 10+ seconds afterwards
      // and definitely mutating; document.documentElement is never swapped
      // the same way, so it doesn't share this failure mode).
      new MutationObserver(scheduleApply).observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {}

    document.addEventListener('focusin', function (e) {
      if (blurTimer) { clearTimeout(blurTimer); blurTimer = null; }
      if (lightboxOpen || dialogOpen()) return;
      if (isMobile() && isField(e.target)) setState('hidden');
    });
    document.addEventListener('focusout', function () {
      if (blurTimer) clearTimeout(blurTimer);
      blurTimer = setTimeout(apply, 180);
    });

    expand.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      openLightbox();
    });
    closeBtn.addEventListener('click', function () { closeLightbox(true); });
    backdrop.addEventListener('click', function () { closeLightbox(true); });

    gallery.addEventListener('animationend', function (e) {
      if (e.animationName === 'tbMorphFade') gallery.classList.remove('tb-morph');
    });

    // Rotating with the lightbox open: close it if we crossed to desktop
    // (>750px) — don't rely solely on a possibly-lagging 'resize'. Otherwise
    // re-fit the mockup to the new size.
    window.addEventListener('orientationchange', function () {
      if (!lightboxOpen) return;
      if (!isMobile()) closeLightbox(false);
      else setTimeout(function () { if (lightboxOpen) setWidth('100%'); }, 300);
    });

    // Teeinblue needs a nudge to size correctly after it renders (mobile only,
    // so it can never write mobile inline styles onto the desktop column).
    if (isMobile()) setWidth('100%');
    setTimeout(function () { if (isMobile()) setWidth('100%'); apply(); }, 150);
    apply();

    // A reload landing already scrolled halfway down the page (the browser's
    // own scroll restoration, not user action) left the preview stuck FULL
    // rather than DOCKED, fixable only by scrolling back to the top and down
    // again. apply() above reads window.pageYOffset at build() time — but
    // build() itself only runs once Teeinblue's slow SPA has injected
    // .tee-gallery-content (start() polls up to 12s for it), and the
    // browser's own scroll restoration isn't guaranteed to have landed by
    // then, or to fire a 'scroll' event this file's listener (bound only
    // once build() runs) would catch either way. A couple of extra
    // fixed-delay checks (window.load, +1200ms) turned out not to be
    // enough in practice — Teeinblue can still be resizing the page, and
    // whatever the browser is doing with scroll restoration, well past
    // either point. Poll instead, for as long as start() itself is willing
    // to wait for Teeinblue (~12s) — apply() is just a scroll-offset read
    // plus a class toggle, cheap enough to run this many times for the one
    // page load it takes to matter, and self-limiting rather than running
    // forever.
    // setState()'s own guard (search "if (next === state) return") is a
    // no-op whenever this poll's apply() call above reaches the SAME
    // conclusion it reached last tick — including "still docked" — so it
    // never calls setWidth() again on those ticks. That is fine as long
    // as nothing else touches the width in between, but confirmed live
    // this file is not the only thing that does: Teeinblue's own SPA
    // keeps resizing/re-rendering .tee-gallery-content well into this
    // same ~12s startup window (the same fact the comment above already
    // leans on to justify polling this long), and it can land after
    // apply() has already made the correct docked/full call, clobbering
    // the width apply() is no longer going to re-assert because state
    // didn't change. Reasserting it directly, unconditionally, every
    // tick regardless of whether apply() thought anything changed is
    // what actually survives that — setWidth() is cheap (two style
    // writes plus a dispatched resize event Teeinblue is already built to
    // react to) and idempotent when nothing needed correcting.
    var settleTries = 0;
    var settleTimer = setInterval(function () {
      settleTries++;
      apply();
      /* !lightboxOpen guard — full review caught a real bug: openLightbox()
         sets lightboxOpen=true and setWidth('100%') directly, WITHOUT
         touching state (it doesn't need to — apply() already special-cases
         lightboxOpen and returns before reaching its own state logic). This
         poll doesn't know that: docked-then-tap-⤢ within the first ~12s of
         page load left state==='docked' from before the lightbox opened, so
         every 400ms tick was reasserting the DOCKED_WIDTH (60%) onto
         .tee-gallery-content while the fullscreen lightbox modal (92vw) sat
         around it — the enlarged preview visibly shrinking to a small image
         inside a big blank card, repeatedly, until this timer's own 12s cap
         or the customer closing it. Skip the reassert entirely while the
         lightbox owns the width. */
      if (!lightboxOpen) {
        if (state === 'docked') setWidth(DOCKED_WIDTH);
        else if (state === 'full') setWidth('100%');
      }
      if (settleTries >= 30) clearInterval(settleTimer);
    }, 400);

    // iOS/Android keyboard handling: recover from a stuck HIDDEN when the
    // keyboard is dismissed without a blur (iOS Done/chevron, Android Back).
    if (window.visualViewport) {
      var vpScheduled = false;
      var applyVp = function () {
        vpScheduled = false;
        if (lightboxOpen) return;
        var kbNow = keyboardOpen();
        if (kbNow) kbWasOpen = true;
        if (kbWasOpen && !kbNow && isMobile() && state === 'hidden') {
          kbWasOpen = false;
          var a = document.activeElement;
          if (a && a.blur) a.blur();
          scheduleApply();
        }
        // Deliberately do NOT pin the sticky bar to vv.offsetTop. During fast /
        // momentum scrolling (and while the mobile address bar collapses)
        // offsetTop briefly reports a non-zero value; writing it onto a
        // position:sticky element unsticks the bar and makes it flicker below
        // the top until the scroll settles. CSS `top:0` already keeps it
        // pinned. We only clear any stray inline top a past state may have left
        // so it can never linger. (When the keyboard is open the preview is in
        // the HIDDEN state anyway, so there was nothing visible to align.)
        if (gallery.style.top) gallery.style.top = '';
      };
      // Coalesce: visualViewport scroll fires many times per fast flick;
      // collapse the burst into one write. setTimeout, not
      // requestAnimationFrame — full review caught this as the same
      // rAF-suspension bug enforce16px()'s schedule() had (search
      // "requestAnimationFrame, not requestAnimationFrame" — no, search
      // "setTimeout, not requestAnimationFrame" — further down this file):
      // rAF can simply never fire while the page isn't painting, and since
      // vpScheduled only resets inside applyVp() itself, a rAF that never
      // fires latches this guard true forever — every later
      // visualViewport resize/scroll becomes a silent no-op, permanently
      // breaking the "keyboard dismissed without blur" recovery below
      // (which exists specifically because focus does NOT leave the field
      // on iOS Done/Android Back, so the ordinary apply()/focusout paths
      // don't substitute for it).
      var vpFix = function () {
        if (vpScheduled) return;
        vpScheduled = true;
        setTimeout(applyVp, 0);
      };
      window.visualViewport.addEventListener('resize', vpFix);
      window.visualViewport.addEventListener('scroll', vpFix);
    }

    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        if (lightboxOpen) closeLightbox(false);
        state = ''; apply();
      }
    });
  }
})();

/**
 * iOS focus-zoom enforcer (mobile only)
 * ---------------------------------------------------------------------------
 * Teeinblue renders its fields at 13.3px/14px; iOS zooms into any focused
 * control < 16px. Setting font-size 16px INLINE with importance always wins.
 * On desktop the inline 16px is removed so Teeinblue's own typography is
 * restored. Scoped to input/textarea/select (NOT contenteditable design-text).
 */
(function enforce16px() {
  'use strict';
  var ROOTS = '#teeinblue-wrapper, #tee-artwork-form, .tee-customize-main-form, .tee-customization-wrapper';
  /* type="file" WAS excluded here on the assumption a file input has no
     text-entry surface, so nothing to zoom into — confirmed live that
     assumption is wrong for this specific input: tapping "Upload Photo"
     (a <label for="tee-photo-layer-XXX">) focuses the real, invisible
     file input under it, and iOS's zoom-on-focus rule fires off THAT
     focus regardless of the input being invisible or having no visible
     caret — the whole page zooms in around it, which reads as "the
     button text went big and bold" since that label is what's actually
     on screen when it happens. Same 16px fix as every other field here,
     just no longer skipped for this one type. */
  var FIELDS = 'input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="color"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="hidden"]), textarea, select';
  var scheduled = false;

  function bump(el) {
    if (el && el.style && parseFloat(getComputedStyle(el).fontSize) < 16) {
      el.style.setProperty('font-size', '16px', 'important');
    }
  }
  function eachField(fn) {
    var roots = document.querySelectorAll(ROOTS);
    for (var i = 0; i < roots.length; i++) {
      var fields = roots[i].querySelectorAll(FIELDS);
      for (var j = 0; j < fields.length; j++) fn(fields[j]);
    }
  }
  function fixAll() {
    scheduled = false;
    if (window.innerWidth > 750) { unbumpAll(); return; }
    eachField(bump);
  }
  function unbumpAll() {
    eachField(function (el) {
      if (el.style && el.style.getPropertyValue('font-size') === '16px') el.style.removeProperty('font-size');
    });
  }
  /* setTimeout, not requestAnimationFrame — confirmed live via a diagnostic
     build that the MutationObserver below was correctly firing (200 calls
     recorded), but rAF never once ran fixAll() off the back of them, so the
     very first schedule() call after boot's initial sweep was the last thing
     that ever happened. rAF is suspended by design on a page that isn't
     currently painting, and there is no requirement here for frame-precise
     timing — this only needs to run once shortly after a burst of Teeinblue's
     own mutations settles, which a plain timer does just as well without
     depending on paint state. */
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(fixAll, 0);
  }
  function boot() {
    fixAll();
    /* document.documentElement, not document.body and not #teeinblue-wrapper.
       Two separate bugs found here, both confirmed live on the "MUMMY Letter
       Tile" product via a temporary diagnostic build:
       1) #teeinblue-wrapper doesn't contain #tee-artwork-form at all (wrapper's
          parent is <body> directly; artwork-form's is a completely separate
          <form> elsewhere on the page — a per-template DOM layout difference,
          not universal). Watching it meant the observer watched a subtree the
          actual fields never mount into.
       2) Switching to document.body wasn't enough either: a diagnostic build
          showed fixAll() ran exactly once, at boot (finding zero matching
          roots — Teeinblue hadn't mounted yet), and never again despite the
          page settling for 10+ seconds afterwards and definitely mutating in
          that window (confirmed live: the field existed by then). A fresh
          MutationObserver created later against document.body at that same
          moment DID fire on a manual test mutation, while the original one
          (attached at boot) did not — meaning document.body itself gets
          replaced wholesale at some point after boot() runs (likely part of
          the theme-preview render pipeline), orphaning any observer already
          attached to the old reference. document.documentElement (<html>) is
          never swapped the same way, so it doesn't share either failure mode.
       Only the reactive focusin fallback below was left running while this
       was broken — which loses the race against iOS's own zoom decision on a
       real tap (see the type="file" note above for why proactive beats
       reactive here), which is why the bug reproduced consistently rather
       than intermittently. */
    try { new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true }); } catch (e) {}
    document.addEventListener('focusin', function (e) {
      if (window.innerWidth <= 750) bump(e.target);
    }, true);
    // Remove/re-apply the inline 16px when crossing the mobile/desktop boundary.
    window.addEventListener('resize', schedule);
  }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
